import type { CSSProperties } from "react";
import { useInView } from "../hooks/useInView";
import { useTypewriter } from "../hooks/useTypewriter";
import { FiArrowUpRight } from "react-icons/fi";

const LOG_LINES = [
  "$ cat methodology.log",
  "[ingest] web3-infra      -> docker, traefik, nestjs, node hardening",
  "[ingest] observability  -> teleport, loki, grafana-alloy, zero-trust",
  "[ingest] digital-brain   -> quartz, cloudflare, ai-ingest, synthesis",
  "[synthesis] ckad         -> application lifecycle, security, 2h exam",
  "[synthesis] cka/cks      -> cluster arch, troubleshooting, hardening",
  "---------------------------------------------------------------",
  "status: nothing here is a tutorial followed once",
];

const OUTPUTS = [
  {
    title: "Ingested",
    body: "Every concept, tool, and pattern I encounter gets ingested into my Digital Brain. Not bookmarks - synthesized knowledge with cross-references and agent-maintained structure.",
    label: "Brain",
    url: "https://brain.rakeshkadam.me",
  },
  {
    title: "Secured",
    body: "Security isn't an afterthought. From Teleport zero-trust access to RBAC hardening and supply chain scanning - every layer is designed with defense in depth.",
    label: "Security",
    url: "https://brain.rakeshkadam.me/",
  },
  {
    title: "Observed",
    body: "You can't secure what you can't see. Built scalable logging with Loki, metrics with Prometheus, and telemetry pipelines with Grafana Alloy for full-stack visibility.",
    label: "Observability",
    url: "https://brain.rakeshkadam.me/",
  },
  {
    title: "Automated",
    body: "Repetition is a bug. Everything repeatable becomes code: CI/CD pipelines, infrastructure provisioning, AI-assisted workflows, and self-healing systems.",
    label: "Automation",
    url: "https://brain.rakeshkadam.me/",
  },
];

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

const Connector = ({ style }: { style?: CSSProperties }) => (
  <div className="flex justify-center py-4" style={style}>
    <div className="flex flex-col items-center">
      <div className="h-5 w-px bg-gradient-to-b from-accent/30 to-transparent" />
      <div className="my-1 h-1.5 w-1.5 rounded-full border border-accent/30" />
      <div className="h-5 w-px bg-gradient-to-b from-transparent to-accent/30" />
    </div>
  </div>
);

const Methodology = () => {
  const { ref: pipelineRef, inView } = useInView({ threshold: 0.12 });
  const typed = useTypewriter(LOG_LINES, inView);

  const fade = (delay: number): CSSProperties =>
    prefersReducedMotion()
      ? {}
      : {
          opacity: inView ? 1 : 0,
          transform: inView ? "translateY(0)" : "translateY(12px)",
          transition: `opacity 0.5s ease-out ${delay}ms, transform 0.5s ease-out ${delay}ms`,
        };

  return (
    <section className="py-12 text-light-text dark:text-text-primary">
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <div className="mb-14 text-center">
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.2em] text-light-muted dark:text-text-faint">
            Methodology
          </p>
          <h2 className="mb-4 text-3xl md:text-4xl font-bold text-light-text dark:text-text-primary tracking-tight">
            How I Work
          </h2>
          <p className="mx-auto max-w-xl text-base md:text-lg text-light-muted dark:text-text-muted leading-relaxed">
            Ingest. Synthesize. Output. A knowledge pipeline where understanding
            evolves - it isn't just documented.
          </p>
        </div>

        <div ref={pipelineRef}>
          {/* PHASE 00: TRIGGER */}
          <div className="mb-5 flex items-baseline gap-3" style={fade(0)}>
            <span className="font-mono text-[32px] font-bold leading-none text-accent/20">
              00
            </span>
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-accent/70">
              trigger
            </span>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div
              className="relative overflow-hidden rounded-xl border border-light-border bg-light-surface p-6 dark:border-border-subtle dark:bg-surface-1 transition-colors hover:border-accent/20"
              style={fade(80)}
            >
              <div
                className="absolute inset-y-0 left-0 w-1 bg-accent/60"
                aria-hidden="true"
              />
              <h3 className="mb-2 text-base font-semibold text-accent">
                Encounter something new
              </h3>
              <p className="text-sm text-light-muted dark:text-text-muted leading-relaxed">
                A new tool, a security pattern, a Kubernetes concept. I don't
                just read - I build the mental model from first principles.
              </p>
            </div>

            <div
              className="relative overflow-hidden rounded-xl border border-light-border bg-light-surface p-6 dark:border-border-subtle dark:bg-surface-1 transition-colors hover:border-accent/20"
              style={fade(160)}
            >
              <div
                className="absolute inset-y-0 left-0 w-1 bg-accent/40"
                aria-hidden="true"
              />
              <h3 className="mb-2 text-base font-semibold text-accent">
                Run it for real
              </h3>
              <p className="text-sm text-light-muted dark:text-text-muted leading-relaxed">
                Full implementation. The debugging, the misconfiguration, the
                parts that never make it into a tutorial. That's where learning
                happens.
              </p>
            </div>
          </div>

          <Connector style={fade(240)} />

          {/* PHASE 01: SYNTHESIS */}
          <div className="mb-5 flex items-baseline gap-3" style={fade(300)}>
            <span className="font-mono text-[32px] font-bold leading-none text-accent/20">
              01
            </span>
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-accent/70">
              synthesis
            </span>
          </div>

          <div className="flex justify-center" style={fade(380)}>
            <a
              href="https://brain.rakeshkadam.me"
              target="_blank"
              rel="noopener noreferrer"
              className="group w-full rounded-2xl border border-accent/20 bg-light-surface p-8 text-center shadow-card transition-all hover:border-accent/40 hover:shadow-glow dark:bg-surface-1 md:w-auto md:min-w-[420px]"
            >
              <h3 className="mb-3 text-xl font-bold text-accent transition group-hover:text-accent-muted">
                Digital Brain
              </h3>
              <p className="mb-4 text-sm text-light-muted dark:text-text-muted leading-relaxed">
                40+ pages and growing. A concept doesn't count as understood until
                it survives contact with what I actually built.
              </p>
              <span className="inline-flex items-center gap-1 font-mono text-xs text-accent transition group-hover:text-accent-muted">
                brain.rakeshkadam.me
                <FiArrowUpRight size={12} />
              </span>
            </a>
          </div>

          <Connector style={fade(440)} />

          {/* PHASE 02: OUTPUT */}
          <div className="mb-5 flex items-baseline gap-3" style={fade(500)}>
            <span className="font-mono text-[32px] font-bold leading-none text-accent/20">
              02
            </span>
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-accent/70">
              output
            </span>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {OUTPUTS.map((card, i) => (
              <div
                key={card.title}
                className="relative flex flex-col overflow-hidden rounded-xl border border-light-border bg-light-surface p-6 dark:border-border-subtle dark:bg-surface-1 transition-colors hover:border-accent/20"
                style={fade(560 + i * 60)}
              >
                <div
                  className="absolute left-0 right-0 top-0 h-0.5 bg-accent/40"
                  aria-hidden="true"
                />
                <h3 className="mb-2 text-base font-semibold text-accent">
                  {card.title}
                </h3>
                <p className="flex-1 text-sm text-light-muted dark:text-text-muted leading-relaxed">
                  {card.body}
                </p>
                <a
                  href={card.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-accent transition hover:text-accent-muted"
                >
                  {card.label}
                  <FiArrowUpRight size={12} />
                </a>
              </div>
            ))}
          </div>
        </div>

        {/* Terminal summary */}
        <div className="mt-16 border-t border-light-border pt-12 dark:border-border-subtle">
          <div className="mx-auto max-w-2xl overflow-hidden rounded-xl border border-light-border dark:border-border-subtle">
            <div className="flex items-center gap-1.5 border-b border-light-border bg-light-surface-2 px-4 py-2.5 dark:border-border-subtle dark:bg-surface-2">
              <span className="h-2.5 w-2.5 rounded-full bg-error/70" />
              <span className="h-2.5 w-2.5 rounded-full bg-warning/70" />
              <span className="h-2.5 w-2.5 rounded-full bg-success/70" />
              <span className="ml-3 font-mono text-xs text-light-muted dark:text-text-faint">
                methodology.log
              </span>
            </div>
            <pre className="min-h-[140px] overflow-x-auto bg-light-surface px-4 py-4 font-mono text-[13px] leading-relaxed text-accent-muted dark:bg-surface-0 dark:text-accent">
              {typed.join("\n")}
              <span className="ml-0.5 inline-block h-3.5 w-2 animate-pulse bg-accent-muted align-middle dark:bg-accent" />
            </pre>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Methodology;
